<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-2.5"
    leave-active-class="transition-opacity duration-300 ease-out"
    leave-to-class="opacity-0"
  >
    <div v-if="shouldShow" class="flex items-center gap-2 mt-2">
      <span
        :class="['inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-medium border', statusClass]"
        role="status"
        aria-live="polite"
      >
        <span class="inline-flex items-center justify-center w-4 h-4">
          <i :class="iconClass"></i>
        </span>
        <span>{{ statusText }}</span>
      </span>

      <button
        v-if="status === 'failed' && canRetry"
        @click="handleRetry"
        :disabled="retrying"
        aria-label="Retry sending message"
        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs transition-all duration-200 bg-nera-accent/10 text-nera-accent border border-nera-accent/30 enabled:hover:bg-nera-accent/20 enabled:hover:border-nera-accent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span class="inline-flex items-center justify-center w-4 h-4">
          <i class="fas fa-redo"></i>
        </span>
        <span>Retry</span>
      </button>
    </div>
  </Transition>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  status: {
    type: String,
    validator: (value) => !value || ['sending', 'sent', 'failed'].includes(value)
  },
  error: String,
  retryCount: {
    type: Number,
    default: 0
  },
  onRetry: Function
})

const retrying = ref(false)
const sentTime = ref(0)

const shouldShow = computed(() => {
  if (!props.status) return false
  if (props.status === 'sent') {
    return Date.now() - sentTime.value < 1000
  }
  return true
})

watch(() => props.status, (newStatus) => {
  if (newStatus === 'sent') {
    sentTime.value = Date.now()
    setTimeout(() => {
      sentTime.value = sentTime.value
    }, 1000)
  }
})

const iconClass = computed(() => {
  switch (props.status) {
    case 'sending':
      return 'fas fa-circle-notch fa-spin'
    case 'sent':
      return 'fas fa-check animate-checkmark'
    case 'failed':
      return 'fas fa-exclamation-circle'
    default:
      return ''
  }
})

const statusText = computed(() => {
  switch (props.status) {
    case 'sending':
      return 'Sending...'
    case 'sent':
      return 'Sent'
    case 'failed':
      return props.error || 'Failed to send'
    default:
      return ''
  }
})

const statusClass = computed(() => {
  switch (props.status) {
    case 'sending':
      return 'bg-nera-muted/10 text-nera-muted border-nera-muted/30'
    case 'sent':
      return 'bg-nera-success/10 text-nera-success border-nera-success/30'
    case 'failed':
      return 'bg-nera-danger/10 text-nera-danger border-nera-danger/30'
    default:
      return ''
  }
})

const canRetry = computed(() => {
  return props.retryCount < 3 && !retrying.value
})

const handleRetry = async () => {
  if (!props.onRetry || !canRetry.value) return

  retrying.value = true
  try {
    await props.onRetry()
  } finally {
    setTimeout(() => {
      retrying.value = false
    }, 500)
  }
}
</script>
